import { useState, memo } from 'react';
import { IoTriangle } from 'react-icons/io5';
import { toast } from 'sonner';
import { FaBookmark, FaRegBookmark } from 'react-icons/fa';
import { IoIosArrowRoundForward } from 'react-icons/io';
import { NormieHeaderRow, NormieDataRow, NormieRowType } from '@shared/types/frontend/tables';
import { abbreviateNumber, toSpecialFixed } from '@/lib/misc/utils';
import { getNetworkLogo } from '@/lib/misc/tokenImageHelper';
import { useSession } from '@/contexts/LitSessionProvider';
import { useMixpanel } from '@/lib/misc/mixpanel';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useSavedToken } from '@/hooks/saved/useSavedToken';
import TokenImage from '@/components/shared/TokenImage';
interface Props {
  index: number;
  style: React.CSSProperties;
  data: {
    items: (NormieHeaderRow | NormieDataRow)[];
    onTokenSelect: (searchResult: any) => void;
    customHandler?: (token: any) => void;
  };
}
const NormieTableRow = memo((p: Props) => {
  const {
    PKP
  } = useSession();
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const {
    checkIsSaved,
    handleSaveSingle,
    handleDeleteTokenFromHome
  } = useSavedToken({});
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const item = p.data.items[p.index];
  if (item.type === NormieRowType.HEADER) {
    return <div style={p.style}>
        <p className={(item as NormieHeaderRow).className}>{(item as NormieHeaderRow).content}</p>
      </div>;
  }
  const rowData = (item as NormieDataRow).data;
  const rowExtraData = (item as NormieDataRow).extraData;
  const {
    tokenItem,
    tokenImg,
    isMultiToken
  } = rowExtraData;
  return <div style={p.style}>
      <TableRow key={`${tokenItem?.name}-${p.index}`} className={'px-4 hover:cursor-pointer hover:bg-genius-blue w-full h-[80px] flex items-center justify-between'} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={() => p.data.onTokenSelect(rowData)}>
        <div className='flex w-full items-center'>
          {/* Token save button */}
          <TableCell className='flex items-center w-[5%] flex-shrink-0'>
            <div onClick={() => {
            if (!PKP) {
              toast.error('Create an account to save tokens to your watchlist.', {
                duration: 5000
              });
              return;
            }
            if (!checkIsSaved(tokenItem?.address, tokenItem?.networkId)) {
              LogToMixpanel({
                pkp: PKP,
                eventName: EVENTS.asset.saveToken,
                category: CATEGORIES.asset,
                metaData: {
                  address: tokenItem?.address
                }
              });
              handleSaveSingle({
                tokenToSave: tokenItem
              });
            } else {
              handleDeleteTokenFromHome(tokenItem);
            }
          }} className='flex items-center mr-4'>
              {checkIsSaved(tokenItem?.address, tokenItem?.networkId) ? <FaBookmark className='w-[12px] h-[12px] text-genius-cream cursor-pointer' /> : <FaRegBookmark className='w-[12px] h-[12px] text-genius-cream cursor-pointer' />}
            </div>
          </TableCell>

          {/* Token image, symbol & name */}
          <TableCell className='flex items-center w-[40%] flex-shrink-0'>
            <div className='flex items-center'>
              <div className='flex items-center gap-3 relative'>
                <TokenImage mainImageSrc={tokenImg?.replace('large', 'small')} badgeImageSrc={isMultiToken ? '/static/tokenlogos/gen.png' : getNetworkLogo(tokenItem?.networkId)} size={32} badgeSize={16} />
                <div className='flex flex-col gap-y-1.5 justify-start text-left'>
                  <div className='text-base/4 text-genius-cream max-w-[190px] truncate'>
                    {tokenItem?.name}
                  </div>
                  <div className='text-xs/3 text-genius-cream/50 flex items-center'>
                    {tokenItem?.symbol}
                    {isMultiToken ? <div className='flex items-center text-[12px] text-genius-cream/50 ml-2'>
                        Details{' '}
                        <IoIosArrowRoundForward className='w-[16px] h-[16px] translate-y-[0.75px]' />
                      </div> : null}
                  </div>
                </div>
              </div>
            </div>
          </TableCell>

          {/* Token price & change */}
          <TableCell className='w-[20%] flex-shrink-0'>
            <div className='flex flex-col gap-y-1 text-left min-w-24'>
              <p className='text-base/4 text-genius-cream text-right'>{`$${parseFloat(rowExtraData.tokenPriceUsd.toString()) >= 1000000 ? abbreviateNumber(rowExtraData.tokenPriceUsd) : toSpecialFixed(rowExtraData.tokenPriceUsd, 2)?.value}`}</p>
              <div className='flex items-center justify-end text-right'>
                {rowExtraData.change >= 0 ? <div className='flex items-center text-right'>
                    <IoTriangle color='#34D399' size={10} />
                    <p className='text-gray-400 ml-1 text-right text-[12px]'>{`${toSpecialFixed(rowExtraData.change * 100, 2)?.value}%`}</p>
                  </div> : <div className='flex items-center text-right'>
                    <IoTriangle color='#FF5B5B' size={10} className='rotate-180' />
                    <p className='text-red-400 ml-1 text-right text-[12px]'>{`${toSpecialFixed(Math.abs(rowExtraData.change * 100), 2).value}%`}</p>
                  </div>}
              </div>
            </div>
          </TableCell>

          {/* Token market cap*/}
          <TableCell className='w-[20%] flex-shrink-0 flex-1'>
            <div className='grid gap-y-1 text-genius-cream text-right'>
              <p className='text-base/4'>${abbreviateNumber(rowExtraData.marketCap)}</p>
              <p className='text-xs opacity-50 whitespace-nowrap'>Market Cap</p>
            </div>
          </TableCell>

          {/* Trade button */}
          <TableCell className='flex-shrink-0'>
            <Button className={`rounded-[8px] w-[84px] h-[36px] px-[26px] py-[16px] ${isHovered ? 'bg-genius-pink text-genius-blue' : 'bg-genius-blue text-genius-cream'}`}>
              Trade
            </Button>
            {/* </Link> */}
          </TableCell>
        </div>
      </TableRow>
    </div>;
});
export default NormieTableRow;
NormieTableRow.displayName = 'NormieTableRow';